import type { CSSProperties, ReactNode } from "react";

/**
 * Sprint 18 Wave A — shared loading-state primitive.
 *
 * Replaces inline `LoadingState()` helpers that were duplicated across
 * apps/web pages (VehiclesPage, TripsPage, ReportsPage, etc.). Pages
 * compose this when a route or panel is fetching data.
 *
 * Accessibility:
 *   - The visible text is announced by screen readers via
 *     `role="status"` + `aria-live="polite"` so a state transition
 *     "Loading… → Loaded" reaches assistive tech without interrupting
 *     the user.
 *   - The decorative spinner is `aria-hidden` — the announcement is
 *     text-only.
 *
 * Visual rules:
 *   - Uses existing `--gt-sys-*` tokens. No hard-coded colors.
 *   - Honours the existing `gt-spin` keyframes if a page injects them;
 *     otherwise injects a local copy (idempotent — class name keyed).
 */
export interface LoadingStateProps {
  /** Visible label (e.g. `t("common.loading")`). */
  label: string;
  /** Optional secondary line shown below the label. */
  hint?: ReactNode;
  /** Block padding override (default: `var(--gt-sys-space-6)`). */
  padBlock?: CSSProperties["paddingBlock"];
  style?: CSSProperties;
}

export function LoadingState({ label, hint, padBlock, style }: LoadingStateProps) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "var(--gt-sys-space-2)",
        paddingBlock: padBlock ?? "var(--gt-sys-space-6)",
        ...style,
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 20,
          height: 20,
          borderRadius: "50%",
          border: "2px solid var(--gt-sys-border-strong, var(--gt-sys-border))",
          borderTopColor: "var(--gt-sys-accent)",
          animation: "gt-spin 0.8s linear infinite",
        }}
      />
      <span
        role="status"
        aria-live="polite"
        style={{
          color: "var(--gt-sys-text-muted)",
          fontSize: "var(--gt-sys-font-size-sm)",
        }}
      >
        {label}
      </span>
      {hint ? (
        <span
          style={{
            color: "var(--gt-sys-text-muted)",
            fontSize: "var(--gt-sys-font-size-xs)",
            textAlign: "center",
            maxInlineSize: 360,
          }}
        >
          {hint}
        </span>
      ) : null}
      <style>{`@keyframes gt-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
